import * as React from "react";
import { createPortal } from "react-dom";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Tooltip } from "../../../components/ui/tooltip";
import { useRunInferenceMutation } from "../api/inferenceApi";
import { AssistantAdapter, EditInfo, useHostAdapter } from "../../../platform";
import { Message } from "../types/inference-request";
import ModelSelect from "./ModelSelect";
import { ChatModel } from "../types/model";
import { buildChatMessages } from "../utils/chatMessageBuilder";
import { parseCitations, stripCitationTags } from "../utils/citationParser";
import { parseArgs } from "../utils/parseArgs";
import { describeError } from "../../../utils/describeError";
import ChatMessageBubble, { ChatMessage } from "./ChatMessageBubble";
import DraftEmptyState from "./DraftEmptyState";
import WorkingCard from "./WorkingCard";
import { ArrowUpIcon, StopSquareIcon, SquarePenIcon } from "../../../components/icons";
import { StatusStep } from "../types/status-step";
import { useEffect, useRef, useState } from "react";

const MAX_TOOL_ROUNDS = 50;

interface DraftChatProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  assistant: AssistantAdapter;
  /**
   * Where the New chat button goes: an element in the pane's own chrome, not in this
   * surface (see the comment at the portal below). The layout owns the row and hands
   * the element down; with none, there is no New chat.
   */
  newChatSlot?: HTMLElement | null;
}

const DraftChat = ({ assistant, newChatSlot }: DraftChatProps) => {
  const [selectedModel, setSelectedModel] = useState<ChatModel | null>(null);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPreparingDocument, setIsPreparingDocument] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<Message[]>([]);
  const [statusSteps, setStatusSteps] = useState<StatusStep[]>([]);
  // Which edit the document is scrolled to, so its card reads as the current one.
  // Cleared when a new turn starts -- the highlight would otherwise point at an edit
  // from two answers ago.
  const [activeEdit, setActiveEdit] = useState<{ message: number; edit: number } | null>(null);
  const lastDocSnapshotRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const stoppedRef = useRef(false);
  const currentInferenceRef = useRef<{ abort: () => void } | null>(null);

  const [runInference, { isLoading }] = useRunInferenceMutation();
  // Which Office host this pane is in: every inference call is billed under it. (Ticket 237)
  const host = useHostAdapter().kind;

  const handleStop = () => {
    stoppedRef.current = true;
    currentInferenceRef.current?.abort();
  };

  // The steps are mirrored in a ref so the finished reply can carry a copy of them:
  // the state value in this closure is stale by the time the reply lands, and the
  // reply is what the user opens later to see what the assistant did.
  const stepsRef = useRef<StatusStep[]>([]);
  const setSteps = (next: StatusStep[]) => {
    stepsRef.current = next;
    setStatusSteps(next);
  };

  const addStep = (label: string) =>
    setSteps([
      ...stepsRef.current.map((s) =>
        s.status === "active" ? { ...s, status: "done" as const } : s
      ),
      { label, status: "active" as const },
    ]);

  const markStepFailed = (error: string) =>
    setSteps(
      stepsRef.current.map((s, i) =>
        i === stepsRef.current.length - 1 && s.status === "active"
          ? { ...s, status: "failed" as const, error }
          : s
      )
    );

  const clearSteps = () => setSteps([]);

  /** The steps so far, with the one still running marked done -- for attaching to a reply. */
  const takeSteps = (): StatusStep[] =>
    stepsRef.current.map((s) => (s.status === "active" ? { ...s, status: "done" as const } : s));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusSteps]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !selectedModel) {
      setError("Please select a model and enter a message.");
      return;
    }

    setError(null);
    stoppedRef.current = false;
    // For the Worked card's "· 12s": the whole turn, document read included.
    const turnStartedAt = Date.now();
    const userMessage: ChatMessage = {
      role: "user",
      content: trimmed,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setPrompt("");

    try {
      setIsPreparingDocument(true);
      setActiveEdit(null);
      clearSteps();
      // Named before the extract, not after it: the extract is the first thing that
      // takes time, and with the spinner living on the step there has to be a step for
      // it to live on from the moment the turn starts.
      addStep("Sending message");
      const docModel = await assistant.extractDocumentModel();

      // Detect document edits since the last turn
      const currentSnapshot = JSON.stringify(docModel);
      let historyForRequest = conversationHistory;
      if (lastDocSnapshotRef.current !== null && currentSnapshot !== lastDocSnapshotRef.current) {
        historyForRequest = [
          ...conversationHistory,
          {
            role: "user",
            content:
              "Note: The document has been modified since your last response. " +
              "Segment IDs and content may have changed. Use only the current " +
              "document state that follows for all references — do not reuse " +
              "segment IDs from earlier tool calls.",
          },
        ];
      }
      lastDocSnapshotRef.current = currentSnapshot;

      const request = await buildChatMessages(
        userMessage.content,
        historyForRequest,
        docModel,
        assistant
      );
      setIsPreparingDocument(false);

      const turnMessages: Message[] = [{ role: "user", content: userMessage.content }];
      const turnEdits: EditInfo[] = [];
      let currentRequest = request;
      let hitToolLimit = false;
      let needsSummary = false;

      // Bookmark chain for sequential insert_paragraph "after" calls
      const lastInsertBookmarks = new Map<string, string>();

      for (let round = 0; round < MAX_TOOL_ROUNDS; round++) {
        if (stoppedRef.current) break;
        // No step for the round itself. A tool used to cost three lines -- the send,
        // the tool, then "Processing tool results" for the round that read it -- so a
        // turn with four edits in it read as twelve steps, most of them about the loop
        // rather than the document. The step a tool call started stays running until
        // the next one starts or the turn ends, which is also what keeps something
        // spinning while the model works out what to do with the result.
        const inferenceCall = runInference({
          modelObjectIdentifier: selectedModel.objectIdentifier,
          request: currentRequest,
          host,
        });
        currentInferenceRef.current = inferenceCall;
        const response = await inferenceCall.unwrap();

        const choice = response.choices?.[0];
        if (!choice) break;

        const assistantMessage = choice.message;

        // ── Tool-call round ──────────────────────────────────────────────
        if (assistantMessage.tool_calls && assistantMessage.tool_calls.length > 0) {
          // Warn AI when approaching limit
          if (round === MAX_TOOL_ROUNDS - 3) {
            // Add context about approaching limit
            currentRequest = {
              ...currentRequest,
              messages: [
                ...currentRequest.messages,
                {
                  role: "user",
                  content:
                    "Notice: You have 3 more tool call rounds remaining. Consider planning your remaining actions efficiently.",
                },
              ],
            };
          } else if (round === MAX_TOOL_ROUNDS - 1) {
            currentRequest = {
              ...currentRequest,
              messages: [
                ...currentRequest.messages,
                {
                  role: "user",
                  content:
                    "Notice: This is your final tool call round. After this, you'll need to provide a summary of progress and indicate if continuation is needed.",
                },
              ],
            };
          }

          const assistantToolMsg: Message = {
            role: "assistant",
            content: assistantMessage.content,
            tool_calls: assistantMessage.tool_calls.map((tc) => ({
              id: tc.id,
              type: "function" as const,
              function: {
                name: tc.function.name,
                arguments:
                  typeof tc.function.arguments === "string"
                    ? tc.function.arguments
                    : JSON.stringify(tc.function.arguments),
              },
            })),
          };
          turnMessages.push(assistantToolMsg);

          const toolResultMessages: Message[] = [];
          for (const call of assistantMessage.tool_calls) {
            const toolLabel = call.function.name
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c: string) => c.toUpperCase());
            addStep(toolLabel);

            // Inject bookmark chain for sequential inserts targeting the same position
            let effectiveArgs: object | string = call.function.arguments;
            if (call.function.name === "insert_paragraph") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (parsed.position === "after" && lastInsertBookmarks.has(key)) {
                effectiveArgs = {
                  ...parsed,
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            } else if (call.function.name === "insert_chained_paragraphs") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (lastInsertBookmarks.has(key)) {
                effectiveArgs = {
                  ...parsed,
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            } else if (call.function.name === "insert_table") {
              const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
              const key = `${parsed.segmentId}:${parsed.position}`;
              if (lastInsertBookmarks.has(key)) {
                // A prior insert in this batch already wrote to this position.
                // Remap to "after" the last bookmark so the table follows that
                // content rather than re-anchoring to the (now-stale) segment index.
                effectiveArgs = {
                  ...parsed,
                  position: "after",
                  insertAfterBookmark: lastInsertBookmarks.get(key),
                };
              } else {
                effectiveArgs = parsed;
              }
            }

            const result = await assistant.executeToolCall(call.function.name, effectiveArgs);
            if (!result.success) markStepFailed(result.error ?? "Unknown error");
            // Update bookmark chain for the next call in this batch
            if (result.success && result.bookmarkName) {
              if (
                call.function.name === "insert_paragraph" ||
                call.function.name === "insert_chained_paragraphs"
              ) {
                const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
                const key = `${parsed.segmentId}:${parsed.position}`;
                lastInsertBookmarks.set(key, result.bookmarkName);
              } else if (call.function.name === "insert_table") {
                // Key off the original (pre-remap) position so subsequent inserts
                // targeting the same logical slot continue to chain correctly.
                const parsed = parseArgs<Record<string, unknown>>(call.function.arguments);
                const key = `${parsed.segmentId}:${parsed.position}`;
                lastInsertBookmarks.set(key, result.bookmarkName);
              }
            }

            toolResultMessages.push({
              role: "tool",
              content: JSON.stringify(result),
              tool_call_id: call.id,
            });

            // Build edit card (single call replaces the old 9 if-blocks)
            const editResult = assistant.describeEdit(
              call.function.name,
              call.function.arguments,
              result,
              docModel
            );
            if (editResult) {
              if (Array.isArray(editResult)) turnEdits.push(...editResult);
              else turnEdits.push(editResult);
            }
          }
          turnMessages.push(...toolResultMessages);

          currentRequest = {
            ...currentRequest,
            messages: [...currentRequest.messages, assistantToolMsg, ...toolResultMessages],
          };

          if (choice.finish_reason === "stop") break;

          // Check if we're about to hit the limit
          if (round === MAX_TOOL_ROUNDS - 1) {
            hitToolLimit = true;
            needsSummary = true;
          }
          continue;
        }

        // ── Final text response ──────────────────────────────────────────
        const reply = assistantMessage.content?.trim();
        const assistantContent =
          reply && reply.length > 0 ? reply : "No response content returned.";

        const { content: cleanContent, citations } = readCitations(assistantContent);
        if (citations.length > 0) await assistant.enrichCitations?.(citations, docModel);

        turnMessages.push({ role: "assistant", content: assistantContent });

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: cleanContent,
            edits: turnEdits.length > 0 ? turnEdits : undefined,
            citations: citations.length > 0 ? citations : undefined,
            model: selectedModel.name,
            timestamp: Date.now(),
            steps: takeSteps(),
            durationMs: Date.now() - turnStartedAt,
          },
        ]);
        clearSteps();
        break;
      }

      // ── Handle tool limit reached ──────────────────────────────────────────
      if (hitToolLimit && needsSummary) {
        try {
          addStep("Generating summary");
          // Make a final call without tools to get summary
          const summaryRequest = {
            messages: [
              ...currentRequest.messages,
              {
                role: "system" as const,
                content:
                  "You have reached the tool call limit. Please provide a clear summary of what you accomplished and whether you would need additional tool rounds to complete the user's request. If more work is needed, offer to continue where you left off.",
              },
            ],
            // No tools array = tools disabled
          };

          const summaryResponse = await runInference({
            modelObjectIdentifier: selectedModel.objectIdentifier,
            request: summaryRequest,
            host,
          }).unwrap();

          const summaryContent =
            summaryResponse.choices?.[0]?.message?.content?.trim() ||
            "I've completed the available tool actions. Some work may remain incomplete due to the tool call limit.";

          const { content: cleanSummaryContent, citations: summaryCitations } =
            readCitations(summaryContent);
          if (summaryCitations.length > 0)
            await assistant.enrichCitations?.(summaryCitations, docModel);

          turnMessages.push({ role: "assistant", content: summaryContent });

          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: cleanSummaryContent,
              edits: turnEdits.length > 0 ? turnEdits : undefined,
              citations: summaryCitations.length > 0 ? summaryCitations : undefined,
              model: selectedModel.name,
              timestamp: Date.now(),
              steps: takeSteps(),
              durationMs: Date.now() - turnStartedAt,
            },
          ]);
          clearSteps();
        } catch (summaryError) {
          console.error("Failed to generate summary:", summaryError);
          // Fallback message
          const fallbackContent =
            "I've completed multiple actions but reached the tool call limit. Some work may remain incomplete. Would you like me to continue where I left off?";
          turnMessages.push({ role: "assistant", content: fallbackContent });
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content: fallbackContent,
              edits: turnEdits.length > 0 ? turnEdits : undefined,
              model: selectedModel.name,
              timestamp: Date.now(),
              steps: takeSteps(),
              durationMs: Date.now() - turnStartedAt,
            },
          ]);
          clearSteps();
        }
      }

      setConversationHistory((prev) => [...prev, ...turnMessages]);

      if (turnEdits.length > 0) {
        const updatedDoc = await assistant.extractDocumentModel();
        lastDocSnapshotRef.current = JSON.stringify(updatedDoc);
      }
    } catch (err) {
      setIsPreparingDocument(false);
      clearSteps();
      if (!stoppedRef.current) {
        setError(describeError(err));
      }
    }
  };

  const handleEditAction = async (
    messageIndex: number,
    editIndex: number,
    action: "accepted" | "rejected"
  ): Promise<boolean> => {
    const edit = messages[messageIndex]?.edits?.[editIndex];
    // Nothing to resolve is not a failure — the bulk handlers must not stop here.
    if (!edit?.bookmarkName || edit.status !== "pending") return true;

    // Absent in hosts with no revisions model. The buttons that call this are hidden
    // there, so this is a guard against a stale render rather than an expected path.
    //
    // False, not true: nothing was resolved, and reporting success for an edit still
    // sitting in the document is the exact failure Ticket 550 fixed. It also stops the
    // bulk handlers, which is what should happen when no edit here can ever resolve.
    const review = assistant.review;
    if (!review) return false;

    const fn = action === "accepted" ? review.acceptEdit : review.rejectEdit;
    const result = await fn(edit.bookmarkName);

    // Only mark the edit resolved when Word confirms nothing is left pending in
    // its span. A partial resolution used to be reported as success, so the chat
    // showed the edit accepted while the redline was still in the document.
    // (Ticket 550)
    if (!result.success) {
      setError(
        result.error ?? `Could not ${action === "accepted" ? "accept" : "reject"} the change.`
      );
      return false;
    }

    setError(null);
    setMessages((prev) =>
      prev.map((msg, mi) =>
        mi === messageIndex && msg.edits
          ? {
              ...msg,
              edits: msg.edits.map((e, ei) => (ei === editIndex ? { ...e, status: action } : e)),
            }
          : msg
      )
    );
    return true;
  };

  const handleJumpToEdit = async (messageIndex: number, editIndex: number) => {
    const edit = messages[messageIndex]?.edits?.[editIndex];
    if (!edit) return;
    setActiveEdit({ message: messageIndex, edit: editIndex });
    await assistant.reveal?.(edit);
  };

  const handleAcceptEdit = (mi: number, ei: number) => handleEditAction(mi, ei, "accepted");
  const handleRejectEdit = (mi: number, ei: number) => handleEditAction(mi, ei, "rejected");

  // Both bulk handlers stop at the first edit that could not be fully resolved,
  // leaving it and the rest pending so the error names a recoverable state
  // instead of scrolling past several silent partial resolutions. (Ticket 550)
  const handleAcceptAll = async (messageIndex: number) => {
    const edits = messages[messageIndex]?.edits;
    if (!edits) return;
    for (let i = 0; i < edits.length; i++) {
      if (edits[i].status === "pending" && edits[i].bookmarkName) {
        if (!(await handleAcceptEdit(messageIndex, i))) return;
      }
    }
  };

  const handleRejectAll = async (messageIndex: number) => {
    const edits = messages[messageIndex]?.edits;
    if (!edits) return;
    for (let i = edits.length - 1; i >= 0; i--) {
      if (edits[i].status === "pending" && edits[i].bookmarkName) {
        if (!(await handleRejectEdit(messageIndex, i))) return;
      }
    }
  };

  const isSending = isLoading || isPreparingDocument;

  const handleNewChat = () => {
    setMessages([]);
    setConversationHistory([]);
    setError(null);
    clearSteps();
  };

  // Citations are offered only where they can be followed. A host with no `reveal`
  // has no way to take the user to the sentence a citation names, so the tags are
  // stripped rather than numbered and the reply carries none -- see platform/types.ts.
  const readCitations = (raw: string) =>
    assistant.reveal
      ? parseCitations(raw, assistant.segmentIdPattern)
      : { content: stripCitationTags(raw, assistant.segmentIdPattern), citations: [] };

  const handleSend = () => void send(prompt);
  const canSend = Boolean(prompt.trim()) && selectedModel !== null && !isSending;

  const hasConversation = messages.length > 0 || conversationHistory.length > 0;

  return (
    <div className="box-border flex h-full flex-col overflow-hidden">
      {/* New chat is not in the composer (Jylo3#122). It used to be a bare "+" beside
          the model picker, and a "+" inside a message box reads as "attach a file" --
          pressing it wiped the conversation with no way back. Everything in the composer
          is now about the message being written, as in the web client's; starting over
          is about the conversation, so it sits in the pane's chrome above it, labelled
          in words: the mode row (where the file name used to be, which told the user
          nothing their title bar did not), or Outlook's "Reading ..." line.
          Always there in Draft, disabled until there is a conversation to leave, so the
          row does not appear or shift the moment the first message is sent. */}
      {newChatSlot &&
        createPortal(
          <Button
            variant="ghost"
            onClick={handleNewChat}
            disabled={isSending || !hasConversation}
            className="-mr-1.5 text-muted-foreground hover:text-strong"
          >
            <SquarePenIcon />
            New chat
          </Button>,
          newChatSlot
        )}

      <div className="min-h-0 flex-1">
        <div className="h-full overflow-y-auto">
          {messages.length === 0 && statusSteps.length === 0 ? (
            <DraftEmptyState
              suggestions={assistant.suggestedPrompts}
              disabled={!selectedModel || isSending}
              onPick={(text) => void send(text)}
            />
          ) : (
            <div className="flex flex-col gap-5 px-3.5 pt-4 pb-2">
              {messages.map((message, index) => (
                <ChatMessageBubble
                  key={`msg-${index}`}
                  message={message}
                  messageIndex={index}
                  assistant={assistant}
                  activeEditIndex={activeEdit?.message === index ? activeEdit.edit : null}
                  onJumpToEdit={handleJumpToEdit}
                  onAcceptEdit={handleAcceptEdit}
                  onRejectEdit={handleRejectEdit}
                  onAcceptAll={handleAcceptAll}
                  onRejectAll={handleRejectAll}
                />
              ))}
              {statusSteps.length > 0 && <WorkingCard steps={statusSteps} live />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      <div className="flex-none px-3.5 pt-2 pb-3.5">
        {error && (
          <Alert severity="error" onClose={() => setError(null)} className="mb-2">
            {error}
          </Alert>
        )}
        {/* One card: the input, then a row with the model and send. The model picker is
            a pill rather than a form field because it is a setting you glance at, not a
            value you fill in. */}
        <div className="rounded-[24px] border border-border bg-card px-2.5 py-2 shadow-xs transition-colors duration-150 focus-within:border-primary">
          <Textarea
            placeholder="Ask Jylo…"
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                if (canSend) handleSend();
              }
            }}
            autoGrow={{ minRows: 1, maxRows: 8 }}
            // The card is the field's outline, so the textarea draws none of its own.
            className="rounded-none border-0 bg-transparent px-1 py-1 text-[13px] leading-[1.5] hover:border-0 focus-visible:border-0 focus-visible:ring-0"
          />
          <div className="mt-2 flex flex-row items-center justify-between">
            <div className="flex min-w-0 flex-row items-center gap-1">
              {/* The pill is the picker's own, not something this strip puts around it:
                  its loading and failure states have to wear the same shape, and only
                  ModelSelect knows it is in one. */}
              <ModelSelect selectedModel={selectedModel} onChange={setSelectedModel} />
            </div>
            {isSending ? (
              <Tooltip title="Stop">
                {/* Ink, not red: stopping a reply is not destructive, and a red disc
                    pulsing beside streaming text read as an alarm. The strongest text
                    colour on the surface is distinct from Send's accent without shouting,
                    and grey-900 is dark on light and light on dark, so the disc inverts
                    with the scheme on its own. */}
                <Button
                  size="icon"
                  aria-label="Stop"
                  onClick={handleStop}
                  className="size-[30px] rounded-full bg-grey-900 text-card hover:bg-grey-700"
                >
                  <StopSquareIcon className="size-4" />
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="icon"
                aria-label="Send"
                onClick={handleSend}
                disabled={!canSend}
                className="size-[30px] rounded-full disabled:bg-grey-200 disabled:text-grey-400 disabled:opacity-100"
              >
                <ArrowUpIcon className="size-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftChat;
